import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { stytchService } from "../utils/stytchService";
import { useNavUtils } from "../utils/navUtils";
import { setAuthenticationSource, AuthenticationSource } from "../store/authPreferencesSlice";
import { getUser, UserStatus } from "../utils/userEndpoints";
import { decodeJwt, ConversationTokenPayload } from "../utils/jwtUtils";
import { encryptAndStoreToken, getDecryptedJwt } from "../utils/tokenEncryptionUtils";
import { setUser } from "../store/userSlice";

import "../styles/components/Login.css";

// localStorage key for persisting email
const STORAGE_EMAIL_KEY = "devLoginEmail";

enum LoginViewState {
  EMAIL = "email",
  VERIFICATION = "verification",
  ERROR = "error",
}

// Helper function to validate email format
const containsEmail = (text: string): boolean => {
  const emailRegex = /[\w.-]+@[\w.-]+\.\w+/;
  return emailRegex.test(text);
};

const Login: React.FC = () => {
  // Load saved email from localStorage, or use empty string
  const getSavedEmail = (): string => {
    try {
      return localStorage.getItem(STORAGE_EMAIL_KEY) || "";
    } catch {
      return "";
    }
  };

  // Login state
  const [currentView, setCurrentView] = useState<LoginViewState>(LoginViewState.EMAIL);
  const [email, setEmail] = useState(getSavedEmail() || import.meta.env.VITE_DEFAULT_EMAIL || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stytchMethodId, setStytchMethodId] = useState<string>("");
  const [logoutComplete, setLogoutComplete] = useState(false);

  // React hooks
  const location = useLocation();
  const { goNav, logoutReset } = useNavUtils();
  const dispatch = useDispatch();

  // Flag to prevent duplicate auto-login attempts
  const autoLoginInitiatedRef = useRef(false);

  // Effect to start from a blank state
  useEffect(() => {
    console.log("[Login] mount, resetting all state");
    let canceled = false;
    logoutReset(true).finally(() => {
      console.log("[Login] mount, finished logout reset");
      if (!canceled) setLogoutComplete(true);
    });
    return () => {
      canceled = true;
    };
  }, [logoutReset]);

  // Check URL parameters and handle direct navigation or error cases
  useEffect(() => {
    if (!logoutComplete) return;

    console.log("[Login] mount, checking token");

    const params = new URLSearchParams(location.search);
    const token = params.get("join_token");

    // Determine login flow based on URL parameters
    if (token) {
      try {
        // For token-based flow, we need to check for anon_user_id which allows bypassing login
        const decodedToken = decodeJwt<ConversationTokenPayload>(token);

        // If token contains anon_user_id, store it and redirect directly to AvatarStreamer
        if (decodedToken.anon_user_id) {
          localStorage.setItem("anon_user_id", decodedToken.anon_user_id);
          localStorage.setItem("join_token", token);
          dispatch(setAuthenticationSource(AuthenticationSource.Token));
          console.log("[Login] navigating to avatarstreamer with token");
          goNav("/avatarstreamer");
        } else if (decodedToken.email) {
          // Pre-fill email from token if available (overrides saved email)
          setEmail(decodedToken.email);
        }
      } catch (err) {
        console.error("[Login] Invalid JWT token:", err);
        // Keep the saved email if token parsing fails
      }
    }
    // If there's no token, we keep the saved email from localStorage
  }, [location.search, goNav, dispatch, logoutComplete]);

  // Effect to check for existing authentication and skip login if possible
  useEffect(() => {
    if (!logoutComplete) return;

    // Skip if we're already in an error state, loading, or auto-login already initiated
    if (currentView === LoginViewState.ERROR || loading || autoLoginInitiatedRef.current) {
      return;
    }

    console.log("[Login] mount, selecting nav");

    // Check if we're already authenticated (have JWT token) and stytch session
    const decryptedJwt = getDecryptedJwt();
    const stytchSession = localStorage.getItem("stytch_session");

    if (decryptedJwt && stytchSession) {
      try {
        // Mark that we've initiated the auto-login to prevent duplicates
        autoLoginInitiatedRef.current = true;

        // Check if we have parameters to determine where to navigate
        const currentParams = new URLSearchParams(location.search);
        const token = currentParams.get("join_token");

        if (token) {
          // If we have a token, navigate to AvatarStreamer
          console.log("[Login] Auto-login: Navigating to AvatarStreamer with params");
          dispatch(setAuthenticationSource(AuthenticationSource.Token));
          goNav("/avatarstreamer");
        } else {
          // No parameters - navigate to manual connection screen
          console.log("[Login] Auto-login: No token, navigating to manual connection screen");
          dispatch(setAuthenticationSource(AuthenticationSource.DevEmail));
          goNav("/manual-connection");
        }
      } catch (err) {
        console.error("[Login] Error decrypting JWT token:", err);
      }
    }
  }, [currentView, loading, goNav, location.search, dispatch, logoutComplete]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      localStorage.setItem(STORAGE_EMAIL_KEY, email);
    } catch (err) {
      console.error("Failed to save email to localStorage:", err);
    }

    const codeLifetimeMinutes = email.endsWith("@persona-ai.ai") ? 10 : 10;

    if (containsEmail(email)) {
      try {
        const response = await stytchService.startEmailLogin(email);
        if (response) {
          console.log("Stytch OTP response:", response);
          setStytchMethodId(response.method_id);        
          setCurrentView(LoginViewState.VERIFICATION);
        } else {
          setError("Unknown error starting email login");
          setLoading(false);
        }
      } catch (err) {
        console.error("Stytch error:", err);
        if (err instanceof Error && err.message.includes("[404] email_not_found")) {
          setError("Unrecognized email");
        } else {
          setError(err instanceof Error ? err.message : "Failed to send verification code");
        }
      } finally {
        setLoading(false);
      }
    } else {
      setError("Please enter a valid email address");
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await stytchService.authenticateCode(verificationCode, stytchMethodId);
      
      if (!response) {
        return;
      }

      const su = stytchService.getUserSync();
      
      // extract the user id from the response
      const userId = (su?.trusted_metadata as { user_id?: string })?.user_id;
      if (!userId) {
        console.error("[Login] No userid in response");
        setError("Error in user data");
        setLoading(false);
        return;
      }

      // Store JWT in localStorage for API authentication
      encryptAndStoreToken(response.session_jwt);

      // Store the session token for future API calls
      localStorage.setItem("stytch_session", response.session_token);

      // Get our user data from the API
      let user = null;
      try {
        user = await getUser(userId);
      } catch (err) {
        console.error("[Login] Error fetching user data:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch user data");
        setLoading(false);
        return;
      }

      // Check if the user is active
      if (user.status !== UserStatus.ACTIVE) {
        console.error("[Login] User is not active:", user.status);
        setError("User not active.");
        setLoading(false);
        return;
      }

      // Write user to redux.
      dispatch(setUser(user));

      // Check if we have parameters to determine where to navigate
      const currentParams = new URLSearchParams(location.search);
      const token = currentParams.get("join_token");

      if (token) {
        // If we have a token, navigate to AvatarStreamer
        console.log("[Login] Navigating to AvatarStreamer with params");
        dispatch(setAuthenticationSource(AuthenticationSource.Token));
        goNav("/avatarstreamer");
      } else {
        // No parameters - navigate to manual connection screen
        console.log("[Login] No token, navigating to manual connection screen");
        dispatch(setAuthenticationSource(AuthenticationSource.DevEmail));
        goNav("/manual-connection");
      }
    } catch (err) {
      console.error("[Login] Code verification error:", err);
      setError(err instanceof Error ? err.message : "Failed to verify code");
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(value);
  };

  if (!logoutComplete) return <div className="login-container app-background"></div>;

  return (
    <div className="login-container app-background">
      {currentView === LoginViewState.ERROR ? (
        // Error view for missing params
        <div className="login-form">
          <div className="error-message">{error || "An error occurred"}</div>
        </div>
      ) : currentView === LoginViewState.EMAIL ? (
        // Email view
        <form className="login-form" onSubmit={handleEmailSubmit}>
          {error && <div className="login-error-message">{error}</div>}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="form-input"
          />
          <button type="submit" className="submit-button">
            {loading ? "Sending..." : "Get started"}
          </button>
        </form>
      ) : loading ? (
        // Loading/redirecting view
        <div className="login-form">
          <div className="verification-info">Setting up connection...</div>
          {error && <div className="login-error-message">{error}</div>}
          <div className="loading-indicator">
            <span>Please wait...</span>
          </div>
        </div>
      ) : (
        // Verification code view
        <form className="login-form" onSubmit={handleCodeSubmit}>
          <div className="verification-info">
            Enter the code sent to:
            <div className="verification-email">{email}</div>
          </div>
          {error && <div className="login-error-message">{error}</div>}
          <input
            type="text"
            value={verificationCode}
            onChange={handleCodeChange}
            placeholder="Enter 6-digit code"
            required
            className="form-input code-input"
          />
          <button type="submit" className="submit-button">
            {loading ? "VERIFYING..." : "VERIFY"}
          </button>
          <button
            type="button"
            onClick={() => {
              setCurrentView(LoginViewState.EMAIL);
              setVerificationCode("");
              setError(null);
              setStytchMethodId("");
            }}
            className="back-button"
          >
            ← Back to Email
          </button>
        </form>
      )}
    </div>
  );
};

export default Login;
